import { Injectable, Injector } from '@angular/core';
import { Booklet, BookletAttachments } from '@common/core/models';
import { EFattureInCloudProformaMode } from '@common/enums/fatture-in-cloud-proforma-mode.enum';
import { EBitfInterceptors } from '@enums';
import {
  IBitfApiRequest,
  IBitfApiResponse,
  IFattureInCloudAddress,
  IFattureInCloudInvoiceImportResponse,
  IFattureInCloudProformaResponse,
} from '@common/interfaces';
import { BitfApiService } from '@common/libs/bitforce/core/services/api/bitf-api.service';
import { from, Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, toArray } from 'rxjs/operators';

/** The populate used by the accounting pages is heavy, so pages are not requested all at once. */
const ALL_PAGES_CONCURRENCY = 3;

@Injectable({
  providedIn: 'root',
})
export class BookletsService extends BitfApiService {
  constructor(public injector: Injector) {
    super('booklets', injector);
  }

  /**
   * Strapi caps the page size server side, so asking for a huge size silently returns the first page
   * only: request it, then fetch the remaining pages with the size the server actually applied.
   */
  getAllPages<T>(requestParams: IBitfApiRequest = {}): Observable<IBitfApiResponse<T[]>> {
    return super.get<T>({ ...requestParams, page: 1, size: Number.MAX_SAFE_INTEGER }).pipe(
      switchMap((firstPage) => {
        const content = firstPage.content || [];
        // NOTE: the applied page size comes from the items received, the requested one is not trustworthy
        const pageSize = content.length;
        const totalItems = firstPage.pagination?.totalItems ?? pageSize;

        if (!pageSize || pageSize >= totalItems) {
          return of(firstPage);
        }

        const lastPage = Math.ceil(totalItems / pageSize);
        const pages = Array.from({ length: lastPage - 1 }, (_, index) => index + 2);

        return from(pages).pipe(
          mergeMap(
            (page) =>
              super
                .get<T>({ ...requestParams, page, size: pageSize })
                .pipe(map((response) => ({ page, response }))),
            ALL_PAGES_CONCURRENCY
          ),
          toArray(),
          map((nextPages) => ({
            ...firstPage,
            content: [
              ...content,
              ...nextPages
                .sort((a, b) => a.page - b.page)
                .flatMap((nextPage) => nextPage.response.content || []),
            ],
          }))
        );
      })
    );
  }

  loadFullBooklet(documentId: string): Observable<IBitfApiResponse<Booklet>> {
    return this.getById<Booklet>({
      id: documentId,
      populate: {
        bookletAttachments: {
          populate: {
            ape: { populate: '*' },
            landRegistry: { populate: '*' },
            deedOfOrigin: { populate: '*' },
            urbanPlanning: { populate: '*' },
            occupancyPermit: { populate: '*' },
            systems: { populate: '*' },
            boiler: { populate: '*' },
            cdu: { populate: '*' },
            latentDefectsInsurance: { populate: '*' },
            technicalReport: { populate: '*' },
            extra: { populate: '*' },
            extraContract: { populate: '*' },
            sellerIdentityCard: { populate: '*' },
            casaDocContract: { populate: '*' },
            apeSurveyorSurvey: { populate: '*' },
            apeSurveyorPictures: { populate: '*' },
            casaDocInvoice: { populate: '*' },
            surveyorInvoice: { populate: '*' },
            extraCasaDocInvoice: { populate: '*' },
            costSummary: { populate: '*' },
            extraCostSummary: { populate: '*' },
            unifiedCostSummary: { populate: '*' },
          },
        },
        accounts: true,
        agent: { populate: '*' },
        technicalReportSurveyor: { populate: '*' },
        apeSurveyor: { populate: '*' },
        extraSurveyor: { populate: '*' },
        seller: { populate: '*' },
        secretaries: { populate: '*' },
        paymentsByRole: {
          populate: {
            ape: true,
            extra: true,
            extraAccount: true,
            technicalReport: true,
            accountants: true,
            secretaries: true,
            accounts: true,
          },
        },
      },
    });
  }

  updateBookletAttachments(
    documentId: string,
    bookletAttachments: BookletAttachments
  ): Observable<IBitfApiResponse<Booklet>> {
    return this.post({
      id: documentId,
      action: 'attachments',
      body: { bookletAttachments },
      disableHideLoader: true,
    });
  }

  createFattureInCloudProforma(
    documentId: string,
    mode: EFattureInCloudProformaMode,
    address: IFattureInCloudAddress
  ): Observable<IBitfApiResponse<IFattureInCloudProformaResponse>> {
    return this.post({
      id: documentId,
      action: 'fatture-in-cloud-proforma',
      body: { mode, address },
      disableHideLoader: true,
      // The caller shows its own toast with the backend message, the generic one would replace it
      headers: [{ headerName: EBitfInterceptors.BITF_API_ERRORS_INTERCEPTOR, value: 'skip' }],
    });
  }

  importFattureInCloudInvoice(
    documentId: string,
    url: string
  ): Observable<IBitfApiResponse<IFattureInCloudInvoiceImportResponse>> {
    return this.post({
      id: documentId,
      action: 'fatture-in-cloud-invoice-import',
      body: { url },
      disableHideLoader: true,
    });
  }

  updateBookletRaw(documentId: string, booklet: Partial<Booklet>): Observable<IBitfApiResponse<Booklet>> {
    return this.put({
      id: documentId,
      action: 'raw',
      body: booklet,
      disableHideLoader: true,
    });
  }

  updateBookletsBulkRaw(
    booklets: { documentId: string; data: Partial<Booklet> }[]
  ): Observable<IBitfApiResponse<Booklet[]>> {
    return this.put({
      action: 'bulk/raw',
      body: booklets,
      disableHideLoader: true,
    });
  }

  getBookletWithPassword(id: string, password: string): Observable<IBitfApiResponse<Booklet>> {
    return this.getById<Booklet>({
      id,
      relation: 'password',
      relationId: password,
    });
  }
}
